import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Switch from '../components/Switch';

const API_URL = 'https://songmoment.onrender.com';

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

let ytApiLoaded = false;
let ytApiLoading = false;
const ytReadyCallbacks = [];

function loadYouTubeAPI() {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiLoading) return new Promise(r => ytReadyCallbacks.push(r));
  ytApiLoading = true;
  return new Promise(resolve => {
    ytReadyCallbacks.push(resolve);
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true; ytApiLoading = false;
      ytReadyCallbacks.forEach(cb => cb());
      ytReadyCallbacks.length = 0;
    };
  });
}

// ── Collection Player ─────────────────────────────────────────
function CollectionPlayer({ moments, startIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [queue] = useState(moments);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [autoNext, setAutoNext] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);
  const autoNextRef = useRef(true);
  const shuffleRef = useRef(false);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => { autoNextRef.current = autoNext; }, [autoNext]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const current = queue[currentIndex];

  const getYoutubeId = (moment) => {
    if (moment?.spotify_track_id?.startsWith('youtube_')) return moment.spotify_track_id.replace('youtube_', '');
    if (moment?.embed_url?.includes('youtube')) return moment.embed_url.split('/embed/')[1]?.split('?')[0];
    return null;
  };

  const playNext = () => {
    if (shuffleRef.current) setCurrentIndex(Math.floor(Math.random() * queue.length));
    else setCurrentIndex(i => (i + 1) % queue.length);
  };

  const playPrev = () => setCurrentIndex(i => (i - 1 + queue.length) % queue.length);

  const initPlayer = (moment) => {
    const videoId = getYoutubeId(moment);
    if (!videoId) return;
    const startSeconds = Math.floor((moment.timestamp_ms || 0) / 1000);
    loadYouTubeAPI().then(() => {
      if (!containerRef.current) return;
      if (playerRef.current?.destroy) playerRef.current.destroy();
      setReady(false); setCurrentTime(startSeconds); setDuration(0);
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: { start: startSeconds, autoplay: 1, controls: 0, disablekb: 1, fs: 0, modestbranding: 1, rel: 0, playsinline: 1 },
        events: {
          onReady: (e) => {
            e.target.seekTo(startSeconds, true); setReady(true);
            const check = setInterval(() => { const d = e.target.getDuration(); if (d > 0) { setDuration(d); clearInterval(check); } }, 300);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              const d = e.target.getDuration(); if (d > 0) setDuration(d);
              intervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  const t = playerRef.current.getCurrentTime(); setCurrentTime(t);
                  const m = queue[currentIndexRef.current];
                  if (m?.end_timestamp_ms && t >= m.end_timestamp_ms / 1000 && autoNextRef.current) playNext();
                }
              }, 500);
            } else if (e.data === window.YT.PlayerState.ENDED) {
              setIsPlaying(false); clearInterval(intervalRef.current);
              if (autoNextRef.current) playNext();
            } else { setIsPlaying(false); clearInterval(intervalRef.current); }
          },
        },
      });
    });
  };

  useEffect(() => { if (current) initPlayer(current); return () => clearInterval(intervalRef.current); }, [currentIndex]);
  useEffect(() => { return () => { clearInterval(intervalRef.current); if (playerRef.current?.destroy) playerRef.current.destroy(); }; }, []);

  const togglePlay = () => {
    if (!playerRef.current || !ready) return;
    if (isPlaying) playerRef.current.pauseVideo(); else playerRef.current.playVideo();
  };

  const handleSeek = (e) => {
    const t = Number(e.target.value); setCurrentTime(t);
    if (playerRef.current?.seekTo) playerRef.current.seekTo(t, true);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const startPercent = duration > 0 ? ((current?.timestamp_ms || 0) / 1000 / duration) * 100 : 0;
  const endPercent = duration > 0 && current?.end_timestamp_ms ? (current.end_timestamp_ms / 1000 / duration) * 100 : null;

  const ctrlBtn = (onClick, children, active = false, size = 36) => ({
    onClick, children,
    style: {
      width: size, height: size, borderRadius: '50%',
      background: active ? 'rgba(212,83,126,0.2)' : 'rgba(255,255,255,0.05)',
      border: active ? '0.5px solid rgba(212,83,126,0.6)' : '0.5px solid rgba(255,255,255,0.1)',
      color: active ? '#D4537E' : 'rgba(245,230,237,0.65)',
      fontSize: size > 40 ? 22 : 16, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s',
    }
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(8,6,8,0.96)', backdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 320, height: 180, pointerEvents: 'none' }}>
        <div ref={containerRef} style={{ width: 320, height: 180 }} />
      </div>
      <div style={{ width: '100%', maxWidth: 480 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <span style={{ fontSize: 10, color: 'rgba(212,83,126,0.7)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>{currentIndex + 1} / {queue.length}</span>
          <button onClick={onClose} style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,237,0.45)', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '6px 16px', cursor: 'pointer' }}>✕ Close</button>
        </div>

        {/* Album art + info */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {current?.album_image
            ? <img src={current.album_image} alt="" style={{ width: 160, height: 160, borderRadius: 16, objectFit: 'cover', marginBottom: 16, boxShadow: '0 8px 40px rgba(212,83,126,0.25)' }} />
            : <div style={{ width: 160, height: 160, borderRadius: 16, background: 'rgba(212,83,126,0.08)', border: '0.5px solid rgba(212,83,126,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, margin: '0 auto 16px', opacity: 0.4 }}>♪</div>
          }
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 22, color: '#f5e6ed', marginBottom: 4 }}>{current?.track_name}</div>
          <div style={{ fontSize: 13, color: 'rgba(245,230,237,0.45)', marginBottom: 6 }}>{current?.artist_name}</div>
          <div style={{ fontSize: 11, color: '#D4537E', letterSpacing: '0.06em' }}>
            ▶ from {formatTime(current?.timestamp_ms || 0)}{current?.end_timestamp_ms ? ` → ${formatTime(current.end_timestamp_ms)}` : ''}
          </div>
          {current?.note && (
            <div style={{ marginTop: 12, padding: '8px 14px', background: 'rgba(212,83,126,0.06)', borderRadius: 10, fontSize: 13, color: 'rgba(245,230,237,0.6)', borderLeft: '2px solid rgba(212,83,126,0.4)', textAlign: 'left', fontStyle: 'italic' }}>"{current.note}"</div>
          )}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ position: 'relative', height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
            {endPercent !== null && (
              <div style={{ position: 'absolute', left: `${startPercent}%`, width: `${endPercent - startPercent}%`, height: '100%', background: 'rgba(212,83,126,0.4)', borderRadius: 2, zIndex: 1 }} />
            )}
            <div style={{ width: `${progressPercent}%`, height: '100%', background: '#D4537E', borderRadius: 2, position: 'relative', zIndex: 2, transition: 'width 0.3s' }} />
          </div>
          <input type="range" min="0" max={duration || 300} value={currentTime} onChange={handleSeek} style={{ width: '100%', marginTop: -18, opacity: 0, cursor: 'pointer', height: 18 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.05em' }}>
            <span>{formatTime(currentTime * 1000)}</span>
            <span>{duration > 0 ? formatTime(duration * 1000) : '--:--'}</span>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 24 }}>
          <button onClick={() => setShuffle(v => !v)} style={{ width: 36, height: 36, borderRadius: '50%', background: shuffle ? 'rgba(212,83,126,0.2)' : 'rgba(255,255,255,0.04)', border: shuffle ? '0.5px solid rgba(212,83,126,0.6)' : '0.5px solid rgba(255,255,255,0.1)', color: shuffle ? '#D4537E' : 'rgba(245,230,237,0.45)', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⇄</button>
          <button onClick={playPrev} style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(245,230,237,0.65)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⏮</button>
          <button onClick={togglePlay} disabled={!ready} style={{ width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #D4537E, #993556)', color: '#fff', border: 'none', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: ready ? 1 : 0.5, boxShadow: ready ? '0 4px 20px rgba(212,83,126,0.4)' : 'none' }}>
            {!ready ? '⏳' : isPlaying ? '⏸' : '▶'}
          </button>
          <button onClick={playNext} style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(245,230,237,0.65)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⏭</button>
          <button onClick={() => setAutoNext(v => !v)} style={{ width: 36, height: 36, borderRadius: '50%', background: autoNext ? 'rgba(212,83,126,0.2)' : 'rgba(255,255,255,0.04)', border: autoNext ? '0.5px solid rgba(212,83,126,0.6)' : '0.5px solid rgba(255,255,255,0.1)', color: autoNext ? '#D4537E' : 'rgba(245,230,237,0.45)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⏩</button>
        </div>

        {/* Queue */}
        <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '0.5px solid rgba(212,83,126,0.15)', overflow: 'hidden', maxHeight: 200, overflowY: 'auto' }}>
          {queue.map((m, i) => (
            <div key={m.id} onClick={() => setCurrentIndex(i)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: i === currentIndex ? 'rgba(212,83,126,0.1)' : 'transparent', borderBottom: i < queue.length - 1 ? '0.5px solid rgba(212,83,126,0.1)' : 'none', transition: 'background 0.15s' }}>
              {m.album_image
                ? <img src={m.album_image} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(212,83,126,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>♪</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: i === currentIndex ? 600 : 400, color: i === currentIndex ? '#D4537E' : 'rgba(245,230,237,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.track_name}</div>
                <div style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)' }}>{m.artist_name} · {formatTime(m.timestamp_ms)}</div>
              </div>
              {i === currentIndex && <span style={{ fontSize: 10, color: '#D4537E' }}>▶</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Collections Page ─────────────────────────────────────────
export default function CollectionsPage() {
  const { user, token } = useApp();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeCollection, setActiveCollection] = useState(null);
  const [playerCollection, setPlayerCollection] = useState(null);
  const [playerIndex, setPlayerIndex] = useState(0);
  const [copied, setCopied] = useState(null);
  const [loadingCollection, setLoadingCollection] = useState(null);

  useEffect(() => { fetchCollections(); }, [user]);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(user ? `${API_URL}/api/collections/user/${user.id}` : `${API_URL}/api/collections`);
      setCollections(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createCollection = async () => {
    if (!newName.trim() || !user) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/collections`, {
        user_id: user.id, name: newName.trim(),
        description: newDesc.trim() || null, is_public: isPublic,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCollections(prev => [{ ...res.data, moment_count: 0, username: user.username }, ...prev]);
      setShowCreate(false); setNewName(''); setNewDesc('');
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const deleteCollection = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await axios.delete(`${API_URL}/api/collections/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCollections(prev => prev.filter(c => c.id !== id));
      if (activeCollection?.id === id) setActiveCollection(null);
    } catch (err) { console.error(err); }
  };

  const openCollection = async (c) => {
    if (activeCollection?.id === c.id) { setActiveCollection(null); return; }
    setLoadingCollection(c.id);
    try {
      const res = await axios.get(`${API_URL}/api/collections/${c.id}`);
      setActiveCollection(res.data);
    } catch (err) { console.error(err); }
    finally { setLoadingCollection(null); }
  };

  const handlePlayAll = async (c) => {
    if (!c.moment_count) return;
    try {
      const res = await axios.get(`${API_URL}/api/collections/${c.id}`);
      if (res.data.moments?.length > 0) { setPlayerCollection(res.data); setPlayerIndex(0); }
    } catch (err) { console.error(err); }
  };

  const playFromMoment = (collection, index) => { setPlayerCollection(collection); setPlayerIndex(index); };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`https://songmoment.pages.dev/collections/${id}`);
    setCopied(id); setTimeout(() => setCopied(null), 2000);
  };

  const removeMoment = async (momentId) => {
    if (!activeCollection) return;
    try {
      await axios.delete(`${API_URL}/api/collections/${activeCollection.id}/moments/${momentId}`);
      setActiveCollection(prev => ({ ...prev, moments: prev.moments.filter(m => m.id !== momentId) }));
      setCollections(prev => prev.map(c => c.id === activeCollection.id ? { ...c, moment_count: c.moment_count - 1 } : c));
    } catch (err) { console.error(err); }
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(212,83,126,0.2)',
    borderRadius: 10, padding: '10px 14px',
    fontSize: 13, color: '#f5e6ed',
    marginBottom: 10, boxSizing: 'border-box',
    outline: 'none', transition: 'border-color 0.2s',
  };

  const pillBtn = (extra = {}) => ({
    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'rgba(245,230,237,0.45)', background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: 20, padding: '6px 16px', cursor: 'pointer',
    transition: 'all 0.2s', ...extra,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#080608' }}>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(8,6,8,0.92)', backdropFilter: 'blur(20px)', borderBottom: '0.5px solid rgba(212,83,126,0.2)', padding: '0 24px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #D4537E, #993556)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff' }}>♪</div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 20, letterSpacing: '0.15em', color: '#f5e6ed', textTransform: 'uppercase' }}>Echo Moments</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/')} style={pillBtn()}>← Feed</button>
            {user && (
              <button onClick={() => setShowCreate(true)} style={pillBtn({ color: '#f5e6ed', borderColor: 'rgba(212,83,126,0.6)' })}>+ New</button>
            )}
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px' }}>
        {/* Page title */}
        <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,83,126,0.7)', marginBottom: 10 }}>Collections</p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 34, color: '#f5e6ed', marginBottom: 6, letterSpacing: '0.02em' }}>
          {user ? 'Your curated playlists' : <em style={{ fontStyle: 'italic' }}>Community collections</em>}
        </h1>
        <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.35)', marginBottom: 32, letterSpacing: '0.06em' }}>
          {user ? 'Save and organise your favourite moments' : 'Public collections from the EchoMoments community'}
        </p>

        {/* Create form */}
        {showCreate && (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(212,83,126,0.25)', borderRadius: 16, padding: 20, marginBottom: 28 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,83,126,0.7)', marginBottom: 16 }}>New Collection</p>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Collection name..."
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'rgba(212,83,126,0.6)'}
              onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.2)'}
            />
            <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description (optional)..." rows={2}
              style={{ ...inputStyle, resize: 'none' }}
              onFocus={e => e.target.style.borderColor = 'rgba(212,83,126,0.6)'}
              onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.2)'}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(245,230,237,0.45)', cursor: 'pointer', letterSpacing: '0.06em' }}>
               import Switch from '../components/Switch';
// ...
<Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
<span style={{ fontSize: 13, color: 'var(--text2)' }}>Public collection</span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowCreate(false)} style={pillBtn()}>Cancel</button>
                <button onClick={createCollection} disabled={!newName.trim() || creating}
                  style={pillBtn({ color: '#f5e6ed', borderColor: 'rgba(212,83,126,0.6)', opacity: !newName.trim() || creating ? 0.4 : 1 })}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Collections list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'rgba(245,230,237,0.35)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading collections...</div>
        ) : collections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.2 }}>♪</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: 'rgba(245,230,237,0.5)', marginBottom: 8 }}>No collections yet</p>
            <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Create your first collection to get started</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {collections.map(c => (
              <div key={c.id}
                style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(212,83,126,0.15)', borderRadius: 20, overflow: 'hidden', transition: 'border-color 0.2s, box-shadow 0.2s', minHeight: 160 }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.35)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(212,83,126,0.08)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.15)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Card top */}
                <div style={{ padding: '20px 20px 14px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ width: 64, height: 64, borderRadius: 14, background: `hsl(${c.name.charCodeAt(0) * 20}, 40%, 18%)`, border: '0.5px solid rgba(212,83,126,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>🎵</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 18, color: '#f5e6ed' }}>{c.name}</span>
                      {!c.is_public && <span style={{ fontSize: 9, color: 'rgba(245,230,237,0.35)', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '2px 7px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Private</span>}
                    </div>
                    {c.description && <p style={{ fontSize: 13, color: 'rgba(245,230,237,0.5)', marginBottom: 6, lineHeight: 1.5 }}>{c.description}</p>}
                    <div style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.04em' }}>
                      {c.moment_count} moment{c.moment_count !== 1 ? 's' : ''} · @{c.username} · {timeAgo(c.created_at)}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, padding: '0 20px 18px', flexWrap: 'wrap' }}>
                  <button onClick={() => openCollection(c)} style={{
                    flex: 1, minWidth: 80, padding: '8px 0',
                    background: activeCollection?.id === c.id ? 'rgba(212,83,126,0.12)' : 'rgba(255,255,255,0.04)',
                    border: `0.5px solid ${activeCollection?.id === c.id ? 'rgba(212,83,126,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 20, fontSize: 11,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: activeCollection?.id === c.id ? '#D4537E' : 'rgba(245,230,237,0.45)',
                    cursor: 'pointer',
                  }}>
                    {loadingCollection === c.id ? '⏳' : activeCollection?.id === c.id ? '✕ Close' : '📋 View'}
                  </button>

                  {c.moment_count > 0 && (
                    <button onClick={() => handlePlayAll(c)} style={{ flex: 1, minWidth: 80, padding: '8px 0', background: 'transparent', border: '0.5px solid rgba(212,83,126,0.6)', borderRadius: 20, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#f5e6ed', cursor: 'pointer' }}>
                      ▶ Play All
                    </button>
                  )}

                  <button onClick={() => copyLink(c.id)} style={{
                    flex: 1, minWidth: 80, padding: '8px 0',
                    background: copied === c.id ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.04)',
                    border: `0.5px solid ${copied === c.id ? 'rgba(74,222,128,0.4)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 20, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: copied === c.id ? '#4ade80' : 'rgba(245,230,237,0.45)', cursor: 'pointer',
                  }}>
                    {copied === c.id ? '✓ Copied' : '🔗 Share'}
                  </button>

                  {user?.id === c.user_id && (
                    <button onClick={() => deleteCollection(c.id)} style={{ padding: '8px 14px', background: 'rgba(248,113,113,0.06)', border: '0.5px solid rgba(248,113,113,0.25)', borderRadius: 20, fontSize: 13, cursor: 'pointer' }}>🗑️</button>
                  )}
                </div>

                {/* Expanded moments */}
                {activeCollection?.id === c.id && (
                  <div style={{ borderTop: '0.5px solid rgba(212,83,126,0.15)', padding: '14px 20px' }}>
                    {activeCollection.moments?.length === 0 ? (
                      <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.3)', textAlign: 'center', padding: '12px 0', letterSpacing: '0.06em' }}>No moments yet — save moments from the feed!</p>
                    ) : (
                      activeCollection.moments.map((m, i) => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < activeCollection.moments.length - 1 ? '0.5px solid rgba(212,83,126,0.1)' : 'none' }}>
                          {m.album_image
                            ? <img src={m.album_image} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                            : <div style={{ width: 38, height: 38, borderRadius: 8, background: 'rgba(212,83,126,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>♪</div>
                          }
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: '#f5e6ed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.track_name}</div>
                            <div style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)' }}>{m.artist_name} · {formatTime(m.timestamp_ms)}</div>
                          </div>
                          <button onClick={() => playFromMoment(activeCollection, i)} style={{ padding: '5px 12px', background: 'transparent', border: '0.5px solid rgba(212,83,126,0.5)', borderRadius: 20, fontSize: 10, color: '#D4537E', cursor: 'pointer', letterSpacing: '0.06em' }}>▶</button>
                          {user?.id === c.user_id && (
                            <button onClick={() => removeMoment(m.id)} style={{ padding: '5px 10px', background: 'rgba(248,113,113,0.06)', border: '0.5px solid rgba(248,113,113,0.25)', borderRadius: 20, fontSize: 11, cursor: 'pointer', color: 'rgba(248,113,113,0.7)' }}>✕</button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '28px 20px', borderTop: '0.5px solid rgba(212,83,126,0.1)', marginTop: 20 }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D4537E', opacity: 0.8 }}>
          ♪ Made by Shubh
        </p>
      </footer>

      {playerCollection?.moments?.length > 0 && (
        <CollectionPlayer moments={playerCollection.moments} startIndex={playerIndex} onClose={() => setPlayerCollection(null)} />
      )}
    </div>
  );
}